import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  ArrowLeft, CheckCircle, XCircle, Calendar, Clock, Target, 
  Award, AlertCircle, Loader2, MessageSquare, Check
} from 'lucide-react';
import { performanceApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

interface EmployeeDetailViewProps {
  employeeId: string;
  year: number;
  onBack: () => void;
}

const EmployeeDetailView: React.FC<EmployeeDetailViewProps> = ({ employeeId, year, onBack }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [managerComments, setManagerComments] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduleNotes, setScheduleNotes] = useState('');

  // Fetch employee details
  const { data: details, isLoading, refetch } = useQuery<any>({
    queryKey: ['employeeDetails', employeeId, year],
    queryFn: () => performanceApi.getEmployeeDetails(employeeId, year),
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: ({ goalId, comments }: { goalId: string; comments?: string }) => 
      performanceApi.approveGoal(goalId, { manager_comments: comments }),
    onSuccess: () => {
      toast({ title: 'Goal Approved', description: 'The goal has been approved successfully.' });
      queryClient.invalidateQueries({ queryKey: ['employeeDetails', employeeId, year] });
      queryClient.invalidateQueries({ queryKey: ['teamOverview'] });
      setShowApproveDialog(false);
      setManagerComments('');
      refetch();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to approve goal', variant: 'destructive' });
    }
  });

  const rejectMutation = useMutation({
    mutationFn: ({ goalId, comments }: { goalId: string; comments?: string }) => 
      performanceApi.rejectGoal(goalId, { manager_comments: comments }),
    onSuccess: () => {
      toast({ title: 'Goal Sent Back', description: 'The goal has been sent back for revision.' });
      queryClient.invalidateQueries({ queryKey: ['employeeDetails', employeeId, year] });
      queryClient.invalidateQueries({ queryKey: ['teamOverview'] });
      setShowRejectDialog(false);
      setManagerComments('');
      refetch();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to reject goal', variant: 'destructive' });
    }
  });

  const scheduleMutation = useMutation({
    mutationFn: ({ goalId, date, notes }: { goalId: string; date: string; notes?: string }) => 
      performanceApi.scheduleOneOnOne(goalId, { scheduled_date: date, notes }),
    onSuccess: () => {
      toast({ title: '1:1 Scheduled', description: 'The 1:1 meeting has been scheduled.' });
      queryClient.invalidateQueries({ queryKey: ['employeeDetails', employeeId, year] });
      queryClient.invalidateQueries({ queryKey: ['teamOverview'] });
      setShowScheduleDialog(false);
      setScheduledDate('');
      setScheduleNotes('');
      refetch();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to schedule 1:1', variant: 'destructive' });
    }
  });

  const completeCycleMutation = useMutation({
    mutationFn: (goalId: string) => performanceApi.completeCycle(goalId),
    onSuccess: () => {
      toast({ title: 'Cycle Completed', description: 'The performance cycle has been marked as complete.' });
      queryClient.invalidateQueries({ queryKey: ['employeeDetails', employeeId, year] });
      queryClient.invalidateQueries({ queryKey: ['teamOverview'] });
      refetch();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to complete cycle', variant: 'destructive' });
    }
  });

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'GOAL_SETTING': return 'bg-blue-100 text-blue-800';
      case 'KPI_DEFINITION': return 'bg-purple-100 text-purple-800';
      case 'MANAGER_REVIEW': return 'bg-orange-100 text-orange-800';
      case 'ONE_ON_ONE': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'NOT_STARTED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleApprove = (goalId: string) => {
    setSelectedGoalId(goalId);
    setShowApproveDialog(true);
  };

  const handleReject = (goalId: string) => {
    setSelectedGoalId(goalId);
    setShowRejectDialog(true);
  };

  const handleSchedule = (goalId: string) => {
    setSelectedGoalId(goalId);
    setShowScheduleDialog(true);
  };

  const handleCompleteCycle = (goalId: string) => {
    completeCycleMutation.mutate(goalId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading employee details...</span>
      </div>
    );
  }

  const employee = details?.employee;
  const goals = details?.goals || [];
  const reviews = details?.reviews || [];
  const summary = details?.summary || {};
  const currentStage = details?.current_stage || 'GOAL_SETTING';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Team
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold">
            {employee?.first_name} {employee?.last_name}
          </h2>
          <p className="text-muted-foreground">
            {employee?.job_title || 'Employee'} • {employee?.department || 'No Department'}
          </p>
        </div>
        <Badge className={getStageColor(currentStage)} variant="outline">
          {currentStage.replace(/_/g, ' ')}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Goals</p>
                <p className="text-2xl font-bold mt-1">{summary.total_goals || 0}</p>
              </div>
              <Target className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold mt-1">{summary.approved_goals || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold mt-1">{summary.pending_goals || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">1:1 Scheduled</p>
                <p className="text-2xl font-bold mt-1">{summary.one_on_one_scheduled ? 'Yes' : 'No'}</p>
              </div>
              <Calendar className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Goals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Goals - {year}</CardTitle>
          <CardDescription>Review and manage employee goals</CardDescription>
        </CardHeader>
        <CardContent>
          {goals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No goals set for {year}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Goal</TableHead>
                  <TableHead>KPIs</TableHead>
                  <TableHead>Target Date</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {goals.map((goal: any) => (
                  <TableRow key={goal.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{goal.title}</div>
                        <div className="text-sm text-muted-foreground">{goal.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {goal.kpi_metrics?.length || 0} KPI(s)
                      </div>
                    </TableCell>
                    <TableCell>
                      {goal.target_date ? new Date(goal.target_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-600 rounded-full" 
                            style={{ width: `${goal.progress || 0}%` }}
                          />
                        </div>
                        <span className="text-sm">{goal.progress || 0}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(goal.status)} variant="outline">
                        {goal.status?.replace(/_/g, ' ') || 'NOT STARTED'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStageColor(goal.workflow_stage)} variant="outline">
                        {goal.workflow_stage?.replace(/_/g, ' ') || 'GOAL SETTING'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {/* Show actions based on workflow stage */}
                        {goal.workflow_stage === 'MANAGER_REVIEW' && !goal.manager_approved && (
                          <>
                            <Button 
                              variant="default" 
                              size="sm"
                              onClick={() => handleApprove(goal.id)}
                              disabled={approveMutation.isPending}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleReject(goal.id)}
                              disabled={rejectMutation.isPending}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Revise
                            </Button>
                          </>
                        )}
                        {goal.workflow_stage === 'ONE_ON_ONE' && !goal.one_on_one_date && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleSchedule(goal.id)}
                            disabled={scheduleMutation.isPending}
                          >
                            <Calendar className="h-4 w-4 mr-1" />
                            Schedule 1:1
                          </Button>
                        )}
                        {goal.workflow_stage === 'ONE_ON_ONE' && goal.one_on_one_date && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleCompleteCycle(goal.id)}
                            disabled={completeCycleMutation.isPending}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Complete
                          </Button>
                        )}
                        {goal.manager_approved && (
                          <Badge variant="default" className="bg-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approved
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reviews Table */}
      {reviews.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Performance Reviews - {year}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Review Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review: any) => (
                  <TableRow key={review.id}>
                    <TableCell className="font-medium">{review.review_type || 'Annual'}</TableCell>
                    <TableCell>
                      {review.review_period_start && review.review_period_end 
                        ? `${new Date(review.review_period_start).toLocaleDateString()} - ${new Date(review.review_period_end).toLocaleDateString()}`
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      {review.overall_rating ? (
                        <Badge variant="outline">{review.overall_rating}/5</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(review.status)} variant="outline">
                        {review.status?.replace(/_/g, ' ') || 'PENDING'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Goal</DialogTitle>
            <DialogDescription>
              Approve this goal and advance it to the 1:1 stage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="comments">Manager Comments (Optional)</Label>
              <Textarea
                id="comments"
                placeholder="Add any feedback or comments..."
                value={managerComments}
                onChange={(e) => setManagerComments(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => selectedGoalId && approveMutation.mutate({ 
                goalId: selectedGoalId, 
                comments: managerComments 
              })}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Approve Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Revision</DialogTitle>
            <DialogDescription>
              Send this goal back to the employee for revision.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-comments">Feedback for Employee</Label>
              <Textarea
                id="reject-comments"
                placeholder="Explain what needs to be revised..."
                value={managerComments}
                onChange={(e) => setManagerComments(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => selectedGoalId && rejectMutation.mutate({ 
                goalId: selectedGoalId, 
                comments: managerComments 
              })}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Request Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule 1:1 Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule 1:1 Meeting</DialogTitle>
            <DialogDescription>
              Schedule a one-on-one meeting to discuss this goal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="schedule-date">Meeting Date</Label>
              <Input
                id="schedule-date"
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-notes">Meeting Notes (Optional)</Label>
              <Textarea
                id="schedule-notes"
                placeholder="Add agenda or discussion points..."
                value={scheduleNotes}
                onChange={(e) => setScheduleNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => selectedGoalId && scheduledDate && scheduleMutation.mutate({ 
                goalId: selectedGoalId, 
                date: scheduledDate,
                notes: scheduleNotes
              })}
              disabled={scheduleMutation.isPending || !scheduledDate}
            >
              {scheduleMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Calendar className="h-4 w-4 mr-2" />
              )}
              Schedule Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDetailView;
